import { Link } from "react-router-dom";
import { ArrowRight, AudioWaveform, Disc3, SlidersHorizontal, Mail } from "lucide-react";
import { FadeUp, Eyebrow, Marquee } from "../components/Reveal";
import { Waveform, EqCurve, SpectrumBars, VerticalLabel } from "../components/AudioArt";

const PORTRAIT = "/empress-portrait-glasses.webp";
const HEADSHOT = "/empress-headshot.webp";
const CONSOLE = "/empress-console-mix.webp";

const VALUES = [
  { icon: AudioWaveform, title: "The Ear", body: "Trained on everything from gospel choirs to trap 808s. Genres change; taste doesn't." },
  { icon: SlidersHorizontal, title: "The Chain", body: "Fluent in Pro Tools and Studio One — a surgical, modern in-the-box chain. Precision, always." },
  { icon: Disc3, title: "The Delivery", body: "Masters, instrumentals, acapellas and stems — versioned and organized in your portal." },
];

export default function About() {
  return (
    <div className="bg-background">
      <section className="pt-24 pb-20 lg:pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid gap-14 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <FadeUp className="relative pb-16 pr-6" data-testid="about-collage">
              <div className="absolute -top-8 -left-8 w-[70%] aspect-[4/3] rounded-3xl overflow-hidden duotone-lilac -rotate-3 shadow-xl">
                <img src={CONSOLE} alt="Empress at the console at Da Cave Studios" className="w-full h-full object-cover" />
              </div>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl ring-1 ring-purple-100 rotate-1">
                <img src={PORTRAIT} alt="Empress, recording and mixing engineer" className="w-full h-full object-cover object-top photo-grade" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent" />
                <SpectrumBars count={16} className="absolute left-5 bottom-5 h-9" />
                <VerticalLabel className="absolute right-4 top-6 h-40 text-white/60">Empress · Nashville</VerticalLabel>
              </div>
              <Waveform className="absolute -left-12 right-0 top-[72%] w-[125%] h-32 pointer-events-none" strands={3} delay={0.4} />
              <div className="absolute -bottom-2 -right-2 sm:right-0 w-36 sm:w-44 rounded-2xl overflow-hidden border-4 border-white shadow-xl rotate-3">
                <img src={HEADSHOT} alt="Empress headshot" className="w-full aspect-square object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 left-4 w-40 rounded-2xl bg-obsidian p-3 shadow-xl -rotate-2 hidden sm:block">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">Vocal EQ</p>
                <EqCurve className="w-full h-auto mt-1" />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-7 lg:pl-6">
            <FadeUp>
              <Eyebrow>Your favorite engineer</Eyebrow>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05]">
                Hi, I'm <span className="italic gold-gradient-text">Empress</span>.
              </h1>
              <p className="font-serif italic text-xl sm:text-2xl text-purple-700 mt-4" data-testid="about-tagline">"Your favorite engineer."</p>
            </FadeUp>
            <FadeUp delay={0.12}>
              <p className="text-slate-600 mt-8 text-base sm:text-lg leading-relaxed max-w-xl">
                I'm a recording and mixing engineer, and music is my life. Mixing is an art — and Mixed By Empress is for artists who want their record handled like a masterpiece.
              </p>
              <p className="text-slate-500 mt-5 text-sm sm:text-base leading-relaxed max-w-xl">
                Every booking is reviewed by me personally. Every mix is versioned, every master is delivered organized, and every client gets a private portal where their music lives — no more digging through text messages for the right WAV.
              </p>
            </FadeUp>
            <div className="mt-12 grid gap-8 sm:grid-cols-3">
              {VALUES.map((v, i) => (
                <FadeUp key={v.title} delay={0.15 + i * 0.1} className="border-t border-purple-100 pt-6" data-testid={`about-value-${i}`}>
                  <v.icon size={20} className="text-gold" />
                  <h3 className="font-serif text-xl mt-3">{v.title}</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">{v.body}</p>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.3} className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/book" data-testid="about-book-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
                Book time with me <ArrowRight size={15} />
              </Link>
              <a href="mailto:MixedByEmpress@gmail.com" data-testid="about-email" className="inline-flex items-center gap-2 text-sm text-purple-700 hover:text-gold transition-colors">
                <Mail size={15} /> MixedByEmpress@gmail.com
              </a>
            </FadeUp>
          </div>
        </div>
      </section>
      <Marquee items={["Patience", "Precision", "Warmth", "Standards", "Care"]} />
    </div>
  );
}
